// NestedSidenav.jsx
import { useState, useMemo, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";

import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { useSoftUIController } from "context";
import { collapseItem, collapseText } from "examples/Sidenav/styles/sidenavCollapse";
import PropTypes from "prop-types";
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Icon,
} from "@mui/material";
import WithPermission from "components/WithPermission";

export function getActiveKeyPath(item, pathname) {
  if (item.route === pathname) {
    return [item.key];
  }
  if (item.children) {
    for (const child of item.children) {
      const childPath = getActiveKeyPath(child, pathname);
      if (childPath) {
        return [item.key, ...childPath];
      }
    }
  }
  return null;
}

export default function NestedSidenav({
  item,
  color,
  level,
  openMultiLevelKeyPath,
  setOpenMultiLevelKeyPath,
}) {
  const { pathname } = useLocation();
  const [controller] = useSoftUIController();
  const { miniSidenav, transparentSidenav } = controller;

  const hasChildren = Array.isArray(item.children) && item.children.length > 0;
  const isNodeActive = Boolean(getActiveKeyPath(item, pathname));

  const isOpenSubMenu = openMultiLevelKeyPath[level] === item.key;

  const toggleSubmenu = (e) => {
    e.preventDefault();
    if (!hasChildren) {
      return;
    }

    const ancestors = openMultiLevelKeyPath.slice(0, level);
    const newPath = isOpenSubMenu ? ancestors : [...ancestors, item.key];

    setOpenMultiLevelKeyPath(newPath);
  };

  const icon = level === 0 ? item.icon : level === 1 ? <Icon>fiber_manual_record</Icon> : null;

  return (
    <List disablePadding component="nav">
      <ListItem disablePadding>
        <NavLink
          to={item.route}
          key={item.key}
          style={{ textDecoration: "none", width: "100%" }}
          onClick={hasChildren ? toggleSubmenu : undefined}
        >
          <ListItemButton
            sx={(theme) => ({
              ...collapseItem(theme, { transparentSidenav }),

              minHeight: 40,
              width: "auto",
              color: isNodeActive ? "#344767" : "#67748e",
              fontWeight: isNodeActive ? "600" : "400",
              "&:hover": {
                backgroundColor: "transparent !important",
              },
            })}
          >
            <ListItemIcon
              sx={{
                minWidth: 34,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: isNodeActive ? "#344767" : "#67748e",
                opacity: isNodeActive ? "1" : "0.8",
                fontSize: isNodeActive ? "10px" : "8px",
                fontWeight: isNodeActive ? "600" : "400",
              }}
            >
              {icon}
            </ListItemIcon>
            <ListItemText
              primary={t(`lang.${item.lang_attr}`)}
              sx={(theme) => collapseText(theme, { miniSidenav, transparentSidenav })}
              primaryTypographyProps={{
                sx: {
                  fontWeight: isNodeActive ? "600 !important" : "400 !important",
                  color: isNodeActive ? "#344767 !important" : "#67748e !important",
                  fontSize: "0.870rem !important",
                  // pl: level >= 2 ? level * 1 - 1.5 : 0,
                },
              }}
            />
            {hasChildren && (isOpenSubMenu ? <ExpandLess /> : <ExpandMore />)}
          </ListItemButton>
        </NavLink>
      </ListItem>

      {hasChildren && (
        <Collapse in={isOpenSubMenu} unmountOnExit>
          {level >= 1 && (
            <Box
              sx={{
                position: "absolute",
                top: 27,
                bottom: 10,
                left: 48.5,
                width: "1px",
                bgcolor: "#ccc",
                borderRadius: 1,
                zIndex: 0,
              }}
            />
          )}
          {item.children.map((child) => (
            <WithPermission
              key={child.key}
              {...(child.user_permission
                ? { userPermission: child.user_permission }
                : { permission: child.permission })}
            >
              <NestedSidenav
                key={child.key}
                item={child}
                color={color}
                lang_attr={child.lang_attr}
                level={level + 1}
                openMultiLevelKeyPath={openMultiLevelKeyPath}
                setOpenMultiLevelKeyPath={setOpenMultiLevelKeyPath}
              />
            </WithPermission>
          ))}
        </Collapse>
      )}
    </List>
  );
}

NestedSidenav.propTypes = {
  item: PropTypes.shape({
    key: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    route: PropTypes.string,
    icon: PropTypes.node,
    children: PropTypes.arrayOf(PropTypes.object),
    lang_attr: PropTypes.string.isRequired,
  }).isRequired,
  color: PropTypes.string.isRequired,
  level: PropTypes.number,
  openMultiLevelKeyPath: PropTypes.arrayOf(PropTypes.string),
  setOpenMultiLevelKeyPath: PropTypes.func,
};

NestedSidenav.defaultProps = {
  level: 0,
  openMultiLevelKeyPath: [],
};
